import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
} from '@mui/material';
import {
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { isCentAmount } from '@hydra/internal';
import { floatVal } from '@jamesgmarks/utilities';
import { toDollarAmount } from '../../../../app-utils';
import { IInvoiceApplication } from './types';

interface IInvoiceApplicationRowProps {
  invApp: IInvoiceApplication;
  index: number;
  creditNoteId: number;
  availableCreditAmount: number;
  onUpdateAmount: (creditNoteId: number, invoiceIndex: number, amount: number) => void;
  onRemoveApplication: (creditNoteId: number, invoiceIndex: number) => void;
}

export const InvoiceApplicationRow: React.FC<IInvoiceApplicationRowProps> = ({
  invApp,
  index,
  creditNoteId,
  availableCreditAmount,
  onUpdateAmount,
  onRemoveApplication,
}) => {
  const invoiceNumber = invApp.invoice?.invoiceNumber ?? invApp.invoiceNumber;
  const outstandingAmount = invApp.invoice?.freshbooksOutstandingAmount != null
    ? floatVal(invApp.invoice.freshbooksOutstandingAmount)
    : null;
  const maxInputAmount = outstandingAmount != null
    ? Math.min(availableCreditAmount, outstandingAmount)
    : availableCreditAmount;
  const isOverOutstanding = outstandingAmount != null && invApp.amount > outstandingAmount;
  const hasInvalidDecimalPlaces = !isCentAmount(invApp.amount);
  const helperText = (
    isOverOutstanding
      ? 'Over outstanding'
      : (hasInvalidDecimalPlaces ? 'Use 2 decimal places' : undefined)
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        bgcolor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body1" fontWeight="medium">
          Invoice #{invoiceNumber ?? 'N/A'}
        </Typography>
        <Typography
          variant="body2"
          color={isOverOutstanding ? 'error' : 'text.secondary'}
        >
          Outstanding: {outstandingAmount != null ? toDollarAmount(outstandingAmount) : 'N/A'}
        </Typography>
      </Box>

      <Box sx={{ mx: 2 }}>
        <TextField
          label="Amount"
          type="number"
          value={invApp.amount ?? 0}
          onChange={(e) => {
            const amount = floatVal(e.target.value);
            onUpdateAmount(
              creditNoteId,
              index,
              Math.max(0, Math.min(maxInputAmount, amount)),
            );
          }}
          inputProps={{
            min: 0,
            max: maxInputAmount,
            step: 0.01,
          }}
          error={isOverOutstanding || hasInvalidDecimalPlaces}
          helperText={helperText}
          size="small"
          sx={{ width: '120px' }}
        />
      </Box>

      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={() => onRemoveApplication(creditNoteId, index)}
        title="Remove this invoice application"
        startIcon={<DeleteIcon />}
      >
        Remove
      </Button>
    </Box>
  );
};

export default InvoiceApplicationRow;
